import React from 'react';
import SignupLink from '@/components/SignupLink';
import { Button } from '@/components/ui/button';
import { Check, ChevronDown } from 'lucide-react';
import styles from './PricingSection.module.css';

const plans = [
  {
    name: 'Basic',
    badge: 'FOR SOLO PRACTITIONERS',
    price: '$20',
    unit: '/mo',
    subtitle: 'Everything you need to get started',
    priceNote: 'Includes ads',
    features: [
      '1 login',
      'Manage up to 35 clients',
      'AI meal plan generation',
      'Client app and CRM included',
      'Email and chatbot support',
      'Client questionnaires and templates',
      'Manual + automated meal plans',
      'Appointments and tele-health with AI summary'
    ],
    cta: 'Get Started'
  },
  {
    name: 'Professional',
    badge: 'POPULAR',
    price: '$30',
    unit: '/mo',
    subtitle: 'Built to scale your practice faster',
    priceNote: 'Pause or cancel anytime.\n7 days money-back guarantee',
    features: [
      '1 login (no ads)',
      'Manage up to 50 clients',
      '4 AI meal plans per client / month',
      'AI meal adherence and reports',
      'Client app, CRM and payments',
      'Manual + automated meal plans',
      'Appointments and tele-health with AI summary'
    ],
    cta: 'Get Started',
    highlight: true
  },
  {
    name: 'Clinic',
    badge: 'BUILT FOR TEAMS',
    price: '$150',
    unit: '/mo',
    subtitle: 'Ideal for clinics with multiple practitioners',
    features: [
      '1-5 logins + 1 admin',
      '50 clients per login',
      'Buy additional logins',
      'AI-powered workflows',
      'No ads',
      'Additional login: $30 per login',
      '4 AI meal plans per client / month',
      'Client app, CRM and payments'
    ],
    cta: 'Get Started'
  },
  {
    name: 'Enterprise',
    badge: 'UP TO 50 LOGINS',
    price: '$5,000',
    unit: '/yr',
    subtitle: 'Full-scale solution for large organizations',
    callout: 'Only plan with Premium Email Support',
    features: [
      '1-50 logins + 1 admin',
      '75 clients per login',
      'No ads',
      '10 AI meal plans per client / month',
      'All AI-powered workflows included',
      'Client app, CRM and payments',
      'Appointments and tele-health with AI summary'
    ],
    cta: 'Get Started'
  }
] as const;

const PricingSection = () => {
  const [expandedPlans, setExpandedPlans] = React.useState<Record<string, boolean>>({});
  const [isMobileAccordion, setIsMobileAccordion] = React.useState(false);

  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 48rem)');
    const updateMode = () => setIsMobileAccordion(mediaQuery.matches);
    updateMode();
    mediaQuery.addEventListener('change', updateMode);

    return () => {
      mediaQuery.removeEventListener('change', updateMode);
    };
  }, []);

  const togglePlan = (planName: string) => {
    if (!isMobileAccordion) {
      return;
    }

    setExpandedPlans(prev => ({
      ...prev,
      [planName]: !prev[planName]
    }));
  };

  return (
    <section id="pricing" className={styles.section}>
      <div className={styles.wrapper}>
        <div className={styles.bgCircleTop} aria-hidden="true" />
        <div className={styles.bgCircleBottom} aria-hidden="true" />

        <p className={styles.eyebrow}>• PRICING</p>
        <h2 className={styles.title}>
          Simple pricing. Built to <span className={styles.titleNoWrap}>scale 3X</span>
        </h2>

        <div className={styles.planGrid}>
          {plans.map((plan, index) => {
            const isExpanded = expandedPlans[plan.name] ?? false;

            return (
              <article
                key={plan.name}
                className={`${styles.planCard} ${'highlight' in plan && plan.highlight ? styles.planCardHighlight : ''}`}
              >
                <button
                  type="button"
                  className={styles.planHeader}
                  aria-expanded={isMobileAccordion ? isExpanded : true}
                  aria-controls={`pricing-plan-features-${index}`}
                  onClick={() => togglePlan(plan.name)}
                >
                  <h3 className={styles.planName}>{plan.name}</h3>
                  <ChevronDown
                    className={`${styles.planChevron} ${isExpanded ? styles.planChevronOpen : ''}`}
                    aria-hidden="true"
                  />
                </button>

                <div
                  className={styles.planBody}
                >
                  <p className={styles.planBadge}>{plan.badge}</p>
                  <p className={styles.subtitle}>{plan.subtitle}</p>
                  <div className={styles.divider} />

                  <div className={styles.priceWrap}>
                    <span className={styles.price}>{plan.price}</span>
                    <span className={styles.unit}>{plan.unit}</span>
                  </div>
                  {'priceNote' in plan ? (
                    <p className={styles.priceNote}>{plan.priceNote}</p>
                  ) : null}
                  {'callout' in plan ? (
                    <p className={styles.callout}>{plan.callout}</p>
                  ) : null}

                  <div
                    id={`pricing-plan-features-${index}`}
                    className={`${styles.planFeatures} ${isExpanded ? styles.planFeaturesExpanded : ''}`}
                  >
                    <ul className={styles.featureList}>
                      {plan.features.map(feature => (
                        <li key={feature}>
                          <Check className={styles.checkIcon} />
                          <span className={styles.featureText}>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Button asChild className={styles.planButton}>
                    <SignupLink>{plan.cta}</SignupLink>
                  </Button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
